import React from 'react';
import { useParams, Link } from 'react-router-dom';

const serviceData = {
  1: {
    title: 'Custom Software Development',
    icon: 'bi-code-slash',
    lead: 'Build scalable web and backend systems with modern stacks—Node.js, React, Python, .NET—and architecture designed for growth.',
    description: [
      'We design, build, and maintain custom applications tailored to your business. From internal tools and dashboards to customer-facing platforms and APIs, we use proven frameworks and best practices so your software is maintainable, testable, and ready to scale.',
      'Our process starts with discovery and architecture. We align on scope, timelines, and tech choices, then deliver in sprints with regular demos and transparent progress updates. We support both greenfield projects and legacy modernization.'
    ],
    highlights: [
      'Web applications (React, Vue, Angular, Next.js)',
      'Backend systems (Node.js, Python, .NET, Java)',
      'REST and GraphQL APIs',
      'Database design and optimization',
      'Agile delivery with bi-weekly demos'
    ],
    duration: '4–16+ weeks',
    engagement: 'Fixed-scope or T&M'
  },
  2: {
    title: 'Cloud & DevOps',
    icon: 'bi-cloud-arrow-up',
    lead: 'Migrate to AWS, Azure, or GCP and automate CI/CD, infrastructure-as-code, and monitoring for reliability and cost control.',
    description: [
      'We help you move workloads to the cloud securely and efficiently. Whether you need a lift-and-shift, refactoring for cloud-native services, or a full platform redesign, we design for resilience, cost, and future flexibility.',
      'Our DevOps practice covers CI/CD pipelines, infrastructure-as-code (Terraform, CloudFormation, Bicep), container orchestration (Kubernetes, ECS), and observability (logging, metrics, tracing). We work in your environment or set up a new one from scratch.'
    ],
    highlights: [
      'Cloud migration (AWS, Azure, GCP)',
      'CI/CD and automation',
      'Infrastructure-as-code',
      'Containers and orchestration',
      'Monitoring and cost optimization'
    ],
    duration: '2–12+ weeks',
    engagement: 'Project or retainer'
  },
  3: {
    title: 'UI/UX Design & Development',
    icon: 'bi-layout-text-window-reverse',
    lead: 'User-centered design, prototypes, and front-end implementation that improve engagement and support your product goals.',
    description: [
      'We combine research, design, and front-end development so your product is intuitive, accessible, and aligned with your brand. Our designers work in Figma and collaborate closely with engineers so designs ship as intended.',
      'We offer discovery workshops, wireframes, high-fidelity mockups, prototypes, design systems, and implementation. We follow WCAG guidelines and test across devices and browsers.'
    ],
    highlights: [
      'User research and journey mapping',
      'Wireframes and high-fidelity designs',
      'Design systems and component libraries',
      'Responsive and accessible front ends',
      'Prototyping and usability testing'
    ],
    duration: '2–8 weeks (design), 4+ weeks (dev)',
    engagement: 'Fixed-scope or T&M'
  },
  4: {
    title: 'Mobile App Development',
    icon: 'bi-phone',
    lead: 'Native and cross-platform mobile apps for iOS and Android, from MVPs to enterprise solutions with secure APIs.',
    description: [
      'We build mobile apps that perform well and feel native. We use React Native or Flutter for cross-platform delivery when it fits, and Swift or Kotlin when you need platform-specific features or maximum performance.',
      'Our mobile work includes consumer apps, B2B field tools, internal productivity apps, and apps that integrate with your existing backend and identity systems. We handle push, offline, and security considerations from the start.'
    ],
    highlights: [
      'iOS (Swift) and Android (Kotlin)',
      'Cross-platform (React Native, Flutter)',
      'API integration and offline support',
      'Push notifications and analytics',
      'App Store and Play Store publishing'
    ],
    duration: '8–20+ weeks',
    engagement: 'Fixed-scope or phased'
  },
  5: {
    title: 'System Integration & APIs',
    icon: 'bi-diagram-3',
    lead: 'Integrate legacy systems, ERPs, CRMs, and third-party services with robust APIs and event-driven architectures.',
    description: [
      'We connect your systems so data flows securely and reliably. Whether you need to sync with Salesforce, SAP, Stripe, or custom APIs, we design integration patterns that scale and are easy to maintain.',
      'Our work includes REST and GraphQL APIs, message queues (SQS, RabbitMQ, Kafka), ETL pipelines, and event-driven designs. We document integrations and provide runbooks for your team.'
    ],
    highlights: [
      'REST, GraphQL, and webhook APIs',
      'ERP, CRM, and SaaS integrations',
      'Event-driven and message-based architectures',
      'ETL and data pipelines',
      'API design and documentation'
    ],
    duration: '2–8 weeks per integration',
    engagement: 'Project or retainer'
  },
  6: {
    title: 'Cybersecurity & Compliance',
    icon: 'bi-shield-check',
    lead: 'Security audits, penetration testing, and compliance support for SOC 2, GDPR, and industry-specific frameworks.',
    description: [
      'We help you assess and improve your security posture. Our engagements include architecture reviews, threat modeling, penetration testing, and remediation guidance. We also support compliance initiatives for SOC 2, GDPR, HIPAA, and similar frameworks.',
      'We work with your engineering and operations teams to prioritize findings, implement controls, and establish processes that keep you secure as you scale.'
    ],
    highlights: [
      'Security assessments and audits',
      'Penetration testing and vulnerability scanning',
      'SOC 2, GDPR, HIPAA guidance',
      'Secure architecture and identity design',
      'Incident response and runbooks'
    ],
    duration: '2–6 weeks (audit), ongoing (compliance)',
    engagement: 'Project or retainer'
  }
};

const ServiceDetails = () => {
  const { id } = useParams();
  const service = serviceData[id];

  if (!service) {
    return (
      <div className="service-details section">
        <div className="container">
          <p>Service not found.</p>
          <Link to="/services">Back to Services</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="service-details section">
      <div className="container">
        <div className="row">
          <div className="col-lg-8">
            <div className="service-content">
              <div className="service-header mb-4">
                <i className={`bi ${service.icon} service-detail-icon`}></i>
                <h2>{service.title}</h2>
                <p className="lead">{service.lead}</p>
              </div>
              {service.description.map((para, i) => (
                <p key={i}>{para}</p>
              ))}
              <h4 className="mt-4">What We Deliver</h4>
              <ul className="service-highlights">
                {service.highlights.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
              <div className="mt-4">
                <Link to="/contact" className="btn btn-primary">Get a Quote</Link>
                <Link to="/services" className="btn btn-outline ms-2">All Services</Link>
              </div>
            </div>
          </div>
          <div className="col-lg-4">
            <div className="sidebar">
              <div className="service-info">
                <h4>Service Information</h4>
                <ul className="service-facts">
                  <li>
                    <span className="fact-label">Typical Duration</span>
                    <span className="fact-value">{service.duration}</span>
                  </li>
                  <li>
                    <span className="fact-label">Engagement Model</span>
                    <span className="fact-value">{service.engagement}</span>
                  </li>
                </ul>
                <p className="mt-3 small">Engagements are scoped after a discovery call. Contact us to discuss your project.</p>
                <Link to="/contact" className="btn btn-primary w-100 mt-3">Contact Us</Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceDetails;
